import dayjs from 'dayjs';
import { t } from 'i18next';

import { IAuthUser } from 'auth/types';
import { NumberHelper } from 'core/helpers';
import { CorporateLocationHelper } from 'corporate/helpers';
import { IPartFinderReel } from 'partFinder/types';
import { ICustomerQuoteUpsertForm } from 'quotes/components/CustomerQuoteUpsert/types';
import { DEFAULT_VALID_UNTIL_OFFSET } from 'quotes/constants';
import { QuoteEntityType } from 'quotes/enums';

import { IReelLineForm } from './types';

// Whole reels read as "1,250 FT"; a part-used reel keeps its decimals.
const REEL_LENGTH_FORMAT = '0,0.[00]';

// The quantity starts empty: the shopper says how much of each reel to quote.
export const getDefaultLines = (reels: IPartFinderReel[]): IReelLineForm[] =>
  (reels ?? []).map((reel) => ({
    quantity: null,
    reelRowId: reel?.id,
    uom: reel?.uom ?? null,
  }));

// Which reel was picked, e.g. "Reel RL002321 - 1,250 FT - Port Huron (Michigan)". A quote line
// has no reel field, so the line keeps it as an internal note. A row that is not a lot has no
// reel to name.
export const getReelNote = (reel: IPartFinderReel): string | null => {
  if (!reel?.reelId) {
    return null;
  }

  const reelLength =
    reel.reelLength > 0
      ? [NumberHelper.format({ amount: reel.reelLength, format: REEL_LENGTH_FORMAT }), reel.uom]
          .filter(Boolean)
          .join(' ')
      : null;

  return [t('labelPartFinderReelX', { reelId: reel.reelId }), reelLength, reel.locationName]
    .filter(Boolean)
    .join(' - ');
};

interface ICreateQuoteDefaultValuesProps {
  user: IAuthUser;
  entityType: QuoteEntityType;
  isAlternateVersionEnabled: boolean;
  omitDefaultSalesLocation: boolean;
  alternateDefaultValues: Partial<ICustomerQuoteUpsertForm>;
}

// The create-quote form's starting values, as the Create Quote drawer sets them for a new quote.
export const getCreateQuoteDefaultValues = ({
  user,
  entityType,
  isAlternateVersionEnabled,
  omitDefaultSalesLocation,
  alternateDefaultValues,
}: ICreateQuoteDefaultValuesProps) => ({
  jobName: '',
  location:
    !omitDefaultSalesLocation &&
    user?.location &&
    CorporateLocationHelper.isSales(user.location, isAlternateVersionEnabled)
      ? { id: user.location.id, name: user.location.name }
      : null,
  validUntil:
    QuoteEntityType.QUOTE === entityType ? dayjs().add(DEFAULT_VALID_UNTIL_OFFSET, 'day') : null,
  setAddressesAsDefaultForContact: false,
  ...alternateDefaultValues,
  shippingLocation: CorporateLocationHelper.getDefaultShipping(
    [user?.shippingLocation],
    isAlternateVersionEnabled
  ),
});
